import { type FC, useMemo } from "react";
import { Dialog, LinearProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useCollectionQuery,
  useUpdateContentMutation,
} from "@/partner/entities/contents";
import { mapCollectionToFormValues, type CollectionFormValues } from "../model";
import { CollectionForm } from "./CollectionForm";

interface EditCollectionDialogProps {
  open: boolean;
  collectionId: string | null;
  onClose: () => void;
  onCompleted?: () => void;
}

// Порт референсного EditCollectionDialog: форма редактирования коллекции
// (данные — общий content(id), сохранение — updateContent).
export const EditCollectionDialog: FC<EditCollectionDialogProps> = ({
  open,
  collectionId,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading } = useCollectionQuery({
    variables: { id: String(collectionId) },
    skip: !collectionId || !open,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const [updateContent, { loading: updating }] = useUpdateContentMutation({
    onCompleted: () => {
      enqueueSnackbar(t("collections.edit.success"), { variant: "success" });
      onCompleted?.();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["CollectionsList"],
  });

  const initialValues = useMemo(
    () => (data ? mapCollectionToFormValues(data.content) : undefined),
    [data]
  );

  const handleSubmit = async (values: CollectionFormValues) => {
    await updateContent({
      variables: { id: String(collectionId), input: values },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {loading && !data && <LinearProgress />}
      {data?.content && (
        <CollectionForm
          title={t("collections.edit.title")}
          loading={loading || updating}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onClose={onClose}
          collectionId={collectionId}
          url={data.content.url ? String(data.content.url) : null}
        />
      )}
    </Dialog>
  );
};
